import { isAnswered } from "./answerValidation";
import type { Answers, Template } from "./types";

export type NewOnlyInput = {
  template: Template;
  version: number;
  addedQuestionIds: readonly string[];
  changedQuestionIds: readonly string[];
  reviewFlags: readonly string[];
  /** The answers when the form opened, so nothing vanishes while it is being answered. */
  initialAnswers: Answers;
  /** Staff have no answers of their own: show what a returning client sees. */
  staffPreview: boolean;
};

/**
 * On version 2 and later, the questions someone who answered before needs to see: new or
 * changed ones, ones flagged for review and required ones still unanswered. `null` means show
 * everything (version 1, a first-time respondent, or nothing left to show).
 */
export function newOnlyQuestionIds({
  template,
  version,
  addedQuestionIds,
  changedQuestionIds,
  reviewFlags,
  initialAnswers,
  staffPreview,
}: NewOnlyInput): Set<string> | null {
  if (version <= 1) return null;
  const added = new Set(addedQuestionIds);
  const answerable = template.sections
    .filter((s) => s.audience !== "info")
    .flatMap((s) => s.questions)
    .filter((q) => q.type !== "info" && !q.deprecated);
  const answeredBefore = staffPreview || answerable.some((q) => !added.has(q.id) && isAnswered(initialAnswers[q.id]));
  if (!answeredBefore) return null;
  const wanted = new Set([...addedQuestionIds, ...changedQuestionIds, ...reviewFlags]);
  const visible = new Set(
    answerable
      .filter((q) => wanted.has(q.id) || (!staffPreview && q.required && !isAnswered(initialAnswers[q.id])))
      .map((q) => q.id),
  );
  return visible.size > 0 ? visible : null;
}
